import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { fetchAuthSession } from 'aws-amplify/auth';
import { useAuth } from '../Auth/AuthContext';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

function Progress() {
  const { isSubscribed } = useAuth();
  const [quizHistory, setQuizHistory] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchQuizHistory = async () => {
      try {
        const session = await fetchAuthSession();
        const token = session.tokens?.idToken?.toString();

        const response = await fetch(`${API_BASE_URL}/api/quiz/history?limit=50`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const data = await response.json();
          setQuizHistory(data.quizzes || []);
          setStats(data.stats || null);
        } else {
          console.error('Failed to fetch quiz history:', response.status);
        }
      } catch (err) {
        console.error('Error fetching quiz history:', err);
        setError('Failed to load quiz history');
      } finally {
        setLoading(false);
      }
    };

    fetchQuizHistory();
  }, []);

  const formatDate = (timestamp) => {
    if (!timestamp) return '—';
    const date = new Date(timestamp * 1000);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatTime = (seconds) => {
    if (!seconds) return '—';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
  };

  const getScoreColor = (percentage) => {
    if (percentage >= 80) return 'text-green-600';
    if (percentage >= 60) return 'text-yellow-600';
    return 'text-red-500';
  };

  return (
    <div className="min-h-screen bg-white font-quicksand">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-study-yellow to-study-pink py-12 px-6">
        <div className="max-w-7xl mx-auto">
          <Link
            to="/academy"
            className="inline-flex items-center text-study-black hover:text-white transition-all mb-4"
          >
            <span className="text-2xl mr-2">←</span>
            <span className="font-bold">Back to Essentials</span>
          </Link>
          <h1 className="text-4xl md:text-5xl font-bold text-study-black mb-4">
            Your Progress
          </h1>
          <p className="text-study-black text-lg">
            Track your study journey and see how far you've come
          </p>
        </div>
      </div>

      {/* Stats Overview */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <StatCard
            icon="📝"
            value={quizHistory.length}
            label="Total Quizzes"
            color="bg-study-teal"
          />
          <StatCard
            icon="🎯"
            value={quizHistory.length > 0 ? `${quizHistory[0].percentage}%` : '—'}
            label="Last Score"
            color="bg-study-pink"
          />
          <StatCard
            icon="📊"
            value={stats?.averageScore ? `${stats.averageScore}%` : '—'}
            label="Average Score"
            color="bg-study-yellow"
          />
          <StatCard
            icon="🏆"
            value={stats?.bestScore ? `${stats.bestScore}%` : '—'}
            label="Best Score"
            color="bg-gradient-to-r from-study-teal to-study-pink"
          />
        </div>

        {/* Performance by Subject */}
        {stats?.byCategory && Object.keys(stats.byCategory).length > 0 && (
          <div className="bg-white rounded-2xl shadow-md p-6 mb-8">
            <h2 className="text-2xl font-bold text-study-black mb-6">
              Performance by Subject
            </h2>
            <div className="space-y-4">
              {Object.entries(stats.byCategory).map(([category, data]) => (
                <div key={category} className="flex items-center">
                  <div className="w-40 md:w-48 font-semibold text-study-black truncate">
                    {category}
                  </div>
                  <div className="flex-1 mx-4">
                    <div className="h-4 bg-gray-200 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-study-teal to-study-pink rounded-full transition-all duration-500"
                        style={{ width: `${data.avgScore}%` }}
                      />
                    </div>
                  </div>
                  <div className="w-20 text-right">
                    <span className={`font-bold ${getScoreColor(data.avgScore)}`}>
                      {data.avgScore}%
                    </span>
                    <span className="text-gray-500 text-sm ml-2">({data.count})</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Quiz History */}
        <div className="bg-white rounded-2xl shadow-md p-6">
          <h2 className="text-2xl font-bold text-study-black mb-6">
            Quiz History
          </h2>

          {loading ? (
            <div className="text-center py-8 text-study-black">
              Loading quiz history...
            </div>
          ) : error ? (
            <div className="text-center py-8 text-red-500">
              {error}
            </div>
          ) : quizHistory.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-6xl mb-4">📚</div>
              <p className="text-study-black text-lg mb-4">
                No quizzes taken yet
              </p>
              <Link
                to="/quiz-bank"
                className="inline-block bg-study-teal text-study-black font-bold px-6 py-3 rounded-lg hover:bg-study-yellow transition-all"
              >
                Take Your First Quiz
              </Link>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b-2 border-gray-200">
                    <th className="text-left py-3 px-2 font-bold text-study-black">Date</th>
                    <th className="text-left py-3 px-2 font-bold text-study-black">Subject</th>
                    <th className="text-center py-3 px-2 font-bold text-study-black">Questions</th>
                    <th className="text-center py-3 px-2 font-bold text-study-black">Score</th>
                    <th className="text-center py-3 px-2 font-bold text-study-black hidden md:table-cell">Time</th>
                  </tr>
                </thead>
                <tbody>
                  {quizHistory.map((quiz, index) => (
                    <tr
                      key={quiz.resultId}
                      className={`border-b border-gray-100 ${index % 2 === 0 ? 'bg-gray-50' : ''}`}
                    >
                      <td className="py-3 px-2 text-study-black">
                        {formatDate(quiz.createdAt)}
                      </td>
                      <td className="py-3 px-2 text-study-black">
                        {quiz.category || 'Mixed'}
                      </td>
                      <td className="py-3 px-2 text-center text-study-black">
                        {quiz.correctAnswers}/{quiz.totalQuestions}
                      </td>
                      <td className="py-3 px-2 text-center">
                        <span className={`font-bold ${getScoreColor(quiz.percentage)}`}>
                          {quiz.percentage}%
                        </span>
                      </td>
                      <td className="py-3 px-2 text-center text-study-black hidden md:table-cell">
                        {formatTime(quiz.timeSpentSeconds)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* CTA for non-subscribers */}
        {!isSubscribed && (
          <div className="mt-8 bg-gradient-to-r from-study-teal to-study-pink rounded-2xl p-8 text-center">
            <h3 className="text-2xl font-bold text-study-black mb-3">
              Unlock Unlimited Practice
            </h3>
            <p className="text-study-black mb-6">
              Get unlimited quizzes, study guides, and flashcards with StudyPass Premium
            </p>
            <Link
              to="/pricing"
              className="inline-block bg-study-yellow text-study-black font-bold py-3 px-8 rounded-full hover:bg-white transition-all transform hover:scale-105 shadow-lg"
            >
              Upgrade to Premium
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}

function StatCard({ icon, value, label, color }) {
  return (
    <div className={`${color} rounded-xl p-4 md:p-6 text-center shadow-md`}>
      <div className="text-3xl mb-2">{icon}</div>
      <div className="text-2xl md:text-3xl font-bold text-study-black">{value}</div>
      <div className="text-sm text-study-black">{label}</div>
    </div>
  );
}

export default Progress;
